import { formatCents } from '@core/money'
import { detailFor } from '@core/calendar'
import { completeTask, type LetterTask } from '@core/letters'
import { describeRepeat } from '@core/taskRepeat'
import { describeWhen, byTime, type Appointment } from '@core/appointments'
import { remainingCents, type Payable } from '@core/payments'
import { Card } from '@design/components'
import { Tags } from '@design/Tags'
import { PaymentCard } from './PaymentCard'
import { useState } from 'react'
import { Overlay } from '@design/Overlay'

/**
 * One day, opened from the grid.
 *
 * *Due today* and *Planned today* are kept apart and never merged (§18). They
 * are different claims — one is what someone else expects of you, the other is
 * what you decided — and adding them together makes a day look twice as heavy
 * as it is. Deadlines get their own list again, because a letter that needs
 * answering is not money.
 *
 * Everything here can be acted on. It used to be a page you could only read:
 * the deadline cards had no control at all, so opening a day told you what was
 * there and then made you go and find it somewhere else. A calendar you can
 * only look at is a calendar you stop opening.
 */
export function DaySheet({
  date,
  payables,
  tasks,
  appointments,
  userId,
  onChanged,
  onOpenAppointment,
  onClose,
}: {
  date: string
  payables: Payable[]
  tasks: LetterTask[]
  appointments: Appointment[]
  userId: string
  onChanged: () => void
  onOpenAppointment: (a: Appointment) => void
  onClose: () => void
}) {
  const [open, setOpen] = useState<Payable | null>(null)
  const detail = detailFor(date, payables, tasks, appointments)

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <span className="font-medium px-2 capitalize">{longDate(date)}</span>
        <button onClick={onClose} className="text-muted px-2 py-2">
          Klaar
        </button>
      </header>

      <div className="flex-1 scroll-area px-4 py-4 pad-safe-bottom">
        {/* First, because it is the only thing here with an hour in it. If you
            have to be somewhere at two, that is the answer to "what is today";
            three invoices are not. */}
        {detail.appointments.length > 0 && (
          <section className="mb-5">
            <h2 className="text-sm text-muted mb-2">
              Afspraken · {detail.appointments.length}
            </h2>
            {[...detail.appointments].sort(byTime).map((a) => (
              <button key={a.id} onClick={() => onOpenAppointment(a)} className="w-full text-left">
                <Card className="mb-2 flex items-baseline gap-3">
                  <span className="shrink-0 tabular-nums text-sm font-medium">
                    {describeWhen(a)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{a.title}</p>
                    {a.location && <p className="text-sm text-muted truncate">{a.location}</p>}
                  </div>
                </Card>
              </button>
            ))}
          </section>
        )}

        <List title="Vervalt vandaag" items={detail.due} onOpen={setOpen} />
        <List title="Gepland vandaag" items={detail.planned} onOpen={setOpen} />

        <TaskList
          title="Deadlines"
          tasks={detail.deadlines.filter((t) => t.kind === 'letter')}
          userId={userId}
          onChanged={onChanged}
        />
        <TaskList
          title="Te doen"
          tasks={detail.deadlines.filter((t) => t.kind !== 'letter')}
          userId={userId}
          onChanged={onChanged}
        />
      </div>

      {open && (
        <PaymentCard
          payable={open}
          userId={userId}
          onClose={() => setOpen(null)}
          onPaid={() => {
            setOpen(null)
            onChanged()
          }}
        />
      )}
    </Overlay>
  )
}

function List({
  title,
  items,
  onOpen,
}: {
  title: string
  items: Payable[]
  onOpen: (p: Payable) => void
}) {
  if (items.length === 0) return null
  const total = items.reduce((sum, i) => sum + remainingCents(i), 0)

  return (
    <section className="mb-5">
      <h2 className="text-sm text-muted mb-2">
        {title} · <span className="tabular-nums">{formatCents(total)}</span>
      </h2>
      {items.map((item) => (
        <button
          key={item.instalmentId}
          onClick={() => !item.isEstimate && onOpen(item)}
          className="w-full text-left"
          disabled={item.isEstimate}
        >
          <Card className={`mb-2 flex items-center gap-3 ${item.isEstimate ? 'border-dashed' : ''}`}>
            <span className="shrink-0 w-4 h-1 rounded-full bg-ink" aria-hidden />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{item.supplierName ?? item.description}</p>
              <Tags
                spaceName={item.spaceName}
                spaceKind={item.spaceKind}
                category={item.category}
              />
            </div>
            <span className="shrink-0 tabular-nums font-medium">
              {item.isEstimate && '± '}
              {formatCents(remainingCents(item))}
            </span>
          </Card>
        </button>
      ))}
    </section>
  )
}

/**
 * The tasks falling on this day, each with the one control that matters.
 *
 * Letters and chores are separated rather than listed together. "De brief van
 * de gemeente" and "vuilnis buiten" both have a date and nothing else in
 * common, and a single list of the two is read as neither.
 */
function TaskList({
  title,
  tasks,
  userId,
  onChanged,
}: {
  title: string
  tasks: LetterTask[]
  userId: string
  onChanged: () => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  if (tasks.length === 0) return null

  return (
    <section className="mb-5">
      <h2 className="text-sm text-muted mb-2">
        {title} · {tasks.length}
      </h2>
      {tasks.map((task) => (
        <Card key={task.id} className="mb-2 flex items-start gap-3">
          <button
            aria-label="Afvinken"
            disabled={busy === task.id}
            onClick={async () => {
              setBusy(task.id)
              try {
                await completeTask(task, userId)
                onChanged()
              } finally {
                setBusy(null)
              }
            }}
            className="shrink-0 mt-0.5 w-7 h-7 rounded-full border-2 border-line active:border-ink"
          />
          <div className="min-w-0">
            <p className="font-medium truncate">{task.title}</p>
            {task.assignmentNote && (
              <p className="text-sm text-muted truncate">{task.assignmentNote}</p>
            )}
            {task.repeatRule && (
              <p className="text-xs text-muted">{describeRepeat(task.repeatRule)}</p>
            )}
          </div>
        </Card>
      ))}
    </section>
  )
}

function longDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T00:00:00`))
}
